import Joi from 'joi';
import { isSectionChildVisible } from './sectionVisibility';

// Flattens fields, tagging fields inside conditional sections with every branch they live in.
// Returns array of { field, sectionConditions } — outermost condition first, empty for unconditional fields.
const flattenFieldsWithMeta = (fields = [], inheritedConditions = []) =>
    fields.reduce((carry, field) => {
        if (field.type === 'section') {
            const children = Array.isArray(field.children) ? field.children : [];
            if (field.sectionMode === 'conditional' && field.dependsOnField) {
                const tagged = children.flatMap((child) =>
                    flattenFieldsWithMeta(child.fields ?? [], [...inheritedConditions, { section: field, child }])
                );
                return [...carry, ...tagged];
            }
            // Non-conditional section — flatten all branches unconditionally
            return [
                ...carry,
                ...children.flatMap((child) => flattenFieldsWithMeta(child.fields ?? [], inheritedConditions))
            ];
        }
        return [...carry, { field, sectionConditions: inheritedConditions }];
    }, []);

// Rule for fields that are currently hidden — anything goes, nothing is demanded.
const makeOptional = () => Joi.any().optional().allow(null, '');

// Builds the address rule: either fully filled, completely empty, or required as a whole.
const buildAddressRule = (required) => {
    const coreFields = ['street', 'streetnumber', 'city', 'postalcode'];
    const baseRule = Joi.object({
        street: Joi.string().allow('', null).label('Straße'),
        streetnumber: Joi.string().allow('', null).label('Hausnummer'),
        city: Joi.string().allow('', null).label('Stadt'),
        postalcode: Joi.string().allow('', null).label('Postleitzahl'),
        country: Joi.string().allow('', null).label('Land'),
        longitude: Joi.number().allow(null),
        latitude: Joi.number().allow(null)
    })
        .unknown()
        .custom((value, helpers) => {
            const address = value ?? {};
            const isFilled = (fieldName) => String(address[fieldName] ?? '').trim().length > 0;
            const filledCount = coreFields.filter(isFilled).length;

            if (filledCount === 0) {
                if (required) return helpers.message('Bitte gebe eine gültige Adresse ein.');
                return value;
            }

            if (filledCount !== coreFields.length) {
                return helpers.message('Bitte gebe eine vollständige Adresse an.');
            }

            return value;
        });

    if (required) {
        return baseRule.required().messages({
            'any.required': 'Bitte gebe eine gültige Adresse ein.'
        });
    }

    return baseRule.optional().allow(null);
};

// Builds the `is` schema of a Joi.when() from the same visibility check the renderer uses.
const buildBranchCondition = ({ section, child }) => {
    const matches = Joi.any().custom((value) => {
        if (!isSectionChildVisible(section, child, value)) throw new Error('condition not met');
        return value;
    });

    // Joi skips rules (and therefore the custom check) on undefined, which would silently activate the
    // branch whenever the controlling field is still empty — mark it required unless empty really matches.
    return isSectionChildVisible(section, child, undefined) ? matches : matches.required();
};

// Wraps a field rule so it only applies while every conditional section it sits in is actually visible.
const wrapWithConditions = (baseRule, sectionConditions = []) =>
    sectionConditions.reduceRight(
        (rule, sectionCondition) =>
            Joi.when(sectionCondition.section.dependsOnField, {
                is: buildBranchCondition(sectionCondition),
                then: rule,
                otherwise: makeOptional()
            }),
        baseRule
    );

// Maps a single form field definition to its Joi rule, including its German error messages.
const ruleBuilder = (field) => {
    switch (field.type) {
        case 'checkbox':
            if (Array.isArray(field.options) && field.options.length > 0) {
                if (field.selectionMode === 'single') {
                    if (field.rules.includes('required')) {
                        return Joi.string().empty('').required().messages({
                            'string.empty': 'Du musst eine Option auswählen.',
                            'any.required': 'Du musst eine Option auswählen.'
                        });
                    }
                    return Joi.string().empty('').allow(null).optional();
                }

                let checkboxRule = Joi.array().items(Joi.string().allow(''));
                if (field.rules.includes('required')) {
                    checkboxRule = checkboxRule.min(1).required().messages({
                        'array.min': 'Du musst mindestens ein Kästchen auswählen.',
                        'any.required': 'Du musst mindestens ein Kästchen auswählen.'
                    });
                } else {
                    checkboxRule = checkboxRule.optional().allow(null);
                }
                return checkboxRule;
            }

            return Joi.alternatives(
                Joi.string()
                    .empty(!field.rules.includes('required') ? '' : null)
                    .messages({
                        'string.empty': 'Das obige Kästchen muss angekreutzt werden.'
                    }),
                Joi.boolean()
                    .invalid(field.rules.includes('required') ? false : null)
                    .messages({
                        'any.invalid': 'Das obige Kästchen muss angekreutzt werden.'
                    })
            );

        case 'file': {
            let fileRule = Joi.number().integer().positive();
            if (field.rules?.includes('required')) {
                fileRule = fileRule.required().messages({
                    'number.base': 'Bitte lade eine Datei hoch.',
                    'any.required': 'Bitte lade eine Datei hoch.'
                });
            } else {
                fileRule = fileRule.optional().allow(null);
            }
            return fileRule;
        }

        case 'date':
            let rule = Joi.date();
            if (field.rules.includes('required')) rule = rule.required();
            return rule;

        case 'address':
            return buildAddressRule(field.rules?.includes('required'));

        case 'table':
            return Joi.array().custom((value, helpers) => {
                let selectedFieldsCount = value.reduce((carry, row) => carry + row.filter((v) => v).length, 0);
                let minRuleIndex = field.rules?.findIndex((rule) => rule.includes('min:'));

                if (minRuleIndex && minRuleIndex !== -1) {
                    let minRuleValue = Number(field.rules[minRuleIndex].split(':')[1]);

                    if (selectedFieldsCount < minRuleValue)
                        return helpers.message('Du musst mindestens ' + minRuleValue + ' Zeit(en) auswählen.');
                }

                return value;
            });

        case 'signature': {
            if (field.rules?.includes('required')) {
                return Joi.string().required().messages({
                    'string.base': 'Bitte unterschreibe das Feld.',
                    'any.required': 'Bitte unterschreibe das Feld.'
                });
            }
            return Joi.string().allow(null, '').optional();
        }

        case 'pricing_table': {
            const minRule = field.rules?.find((r) => typeof r === 'string' && r.startsWith('min:'));
            const min = minRule ? Number(minRule.split(':')[1]) : 0;

            if (field.selectionMode === 'single') {
                const singleRule = Joi.alternatives([
                    Joi.string(),
                    Joi.object({ id: Joi.string().required(), quantity: Joi.number() }),
                    Joi.array().items(
                        Joi.alternatives([
                            Joi.string(),
                            Joi.object({ id: Joi.string().required(), quantity: Joi.number() })
                        ])
                    )
                ]);
                return field.rules?.includes('required')
                    ? singleRule.required().messages({ 'alternatives.match': 'Bitte eine Position auswählen.' })
                    : singleRule.optional().allow(null, '');
            }

            const item = Joi.alternatives([
                Joi.string(),
                Joi.object({ id: Joi.string().required(), quantity: Joi.number() })
            ]);
            let arrRule = Joi.array().items(item);
            if (min > 0) {
                arrRule = arrRule
                    .min(min)
                    .required()
                    .messages({
                        'array.min': `Bitte mindestens ${min} Position${min > 1 ? 'en' : ''} auswählen.`,
                        'any.required': `Bitte mindestens ${min} Position${min > 1 ? 'en' : ''} auswählen.`
                    });
            } else {
                arrRule = arrRule.optional().allow(null);
            }
            return arrRule;
        }

        case 'payment_options': {
            // the answer is the option's price, which the app stored as a number until mid 2026
            const priceRule = Joi.alternatives(Joi.string(), Joi.number());
            return field.rules?.includes('required') ? priceRule.empty('').required() : priceRule.allow(null, '');
        }

        default:
            return field.rules?.length > 0
                ? field.rules.reduce((ruleCarry, currentRule) => {
                      if (currentRule === 'required') return ruleCarry.required();
                      return ruleCarry;
                  }, Joi.string().empty(''))
                : Joi.string().allow(null, '');
    }
};

// Builds the flat Joi object schema (keyed by field name) for a form definition.
// unknown(): stored answers keep the keys of fields deleted from the form since, and their
// "not allowed" error has no input to show up at, so it blocked the submit without a message.
const validationSchemaFromFields = (fields) =>
    Joi.object(
        flattenFieldsWithMeta(fields)
            .filter(({ field }) => !['wysiwyg', 'section'].includes(field.type))
            .reduce((fieldCarry, { field, sectionConditions }) => {
                const baseRule = ruleBuilder(field);
                const finalRule = wrapWithConditions(baseRule, sectionConditions);
                return {
                    ...fieldCarry,
                    [field.name]: field.label ? finalRule.label(field.label) : finalRule
                };
            }, {})
    ).unknown();

export default validationSchemaFromFields;
